import { Component, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { BoiteDialogueGeneriqueComponent, BoiteDialogueMessagesComponent, FonteIconeComponent, SimpleLibelleComponent } from '../../../../../../../val-ammon-composants/val-composants-lib/src/public-api';
import { StatutManuelTransitionResultat } from '../../../StatutManuelTransitionResultat';
import { MajStatutPieceRequest } from '../../../MajStatutPieceRequest';
import { EFactVenteService } from '../../../val-efact-vente.service';

// Boîte de dialogue de modification manuelle du statut d'une facture électronique de vente
@Component(
	{
		selector: 'val-modifier-statut-dialogue',
		imports:
		[
			TranslocoModule,
			FormsModule,
			KENDO_DROPDOWNS,
			BoiteDialogueGeneriqueComponent,
			BoiteDialogueMessagesComponent,
			FonteIconeComponent,
			SimpleLibelleComponent
		],
		templateUrl: './modifier-statut-dialogue.component.html',
		styleUrl: './modifier-statut-dialogue.component.scss'
	})
export class ModifierStatutDialogueComponent {

	// Clés des messages d'information affichés en fonction du statut cible sélectionné
	private static readonly ClesMessagesInformationParStatut: Record<string, string> = {
		"7d7aeaf5-fb99-41e5-ab6a-60c7a44c4458": "vente.modifier-statut.info-transmissible",
		"7a87043a-f5f0-4fcc-a4c7-2b3783d5041a": "vente.modifier-statut.info-non-transmissible"
	};

	// Création
	constructor(private eFactVenteService: EFactVenteService, private translocoService: TranslocoService) {
		this.transitions = [];
		this.statutSelectionne = null;
	}

	// Boîte de dialogue
	@ViewChild("boiteDialogue")
	public boiteDialogue!: BoiteDialogueGeneriqueComponent<boolean>;

	// Boîte de dialogue message (statut modifié entre-temps)
	@ViewChild("boiteDialogueMessages")
	public boiteDialogueMessages!: BoiteDialogueMessagesComponent;

	// Transitions de statuts cibles proposées
	public transitions: StatutManuelTransitionResultat[];

	// Statut cible sélectionné
	public statutSelectionne: string | null;

	// Identifiant de la pièce en cours de modification
	private pieceId!: string | number;

	// Ouvre la boîte de dialogue
	//  Retourne
	//    Vrai si la fiche doit être rafraîchie (sauvegarde effectuée ou statut modifié entre-temps), faux si annulé
	public async Ouvrir(pieceId: string | number): Promise<boolean> {
		this.pieceId = pieceId;
		this.statutSelectionne = null;
		this.transitions = [];

		const valApiResultat = await this.eFactVenteService.RecupererTransitionsModificationStatut(pieceId);
		if (valApiResultat.status === "success" && valApiResultat.resultat) {
			this.transitions = valApiResultat.resultat;
			this.statutSelectionne = this.transitions.length == 1 ? this.transitions[0].id : null;
		}

		return this.boiteDialogue.Ouvrir();
	}

	// Retourne la clé de traduction du message d'information à afficher pour le statut cible actuellement sélectionné, ou null si aucun
	public CleMessageInformationStatut(): string | null {
		if (!this.statutSelectionne)
			return null;

		return ModifierStatutDialogueComponent.ClesMessagesInformationParStatut[this.statutSelectionne.toLowerCase()] ?? null;
	}

	// Valide la modification de statut
	public async Valider(): Promise<void> {
		if (!this.statutSelectionne)
			return;

		const valApiResultat = await this.eFactVenteService.ModifierStatutManuel(this.pieceId, this.statutSelectionne);
		const modificationReussie = valApiResultat.status === "success" && valApiResultat.resultat?.sauvegardeImpossible === false;

		if (valApiResultat.status === "success" && valApiResultat.resultat?.sauvegardeImpossible) {
			this.boiteDialogueMessages.titre = this.translocoService.translate("vente.modifier-statut.modification-impossible-titre");
			this.boiteDialogueMessages.iconeFonte = "fas fa-exclamation-triangle";
			this.boiteDialogueMessages.iconeCouleur = "#d79620";
			this.boiteDialogueMessages.messages = [this.translocoService.translate("vente.modifier-statut.modification-impossible-message")];

			await this.boiteDialogueMessages.Ouvrir();
		}

		this.boiteDialogue.fonctionRetour(true);
		this.boiteDialogue.Fermer();

		if (modificationReussie) {
			const majStatutPieceRequest = new MajStatutPieceRequest();
			majStatutPieceRequest.pieceIds = [String(this.pieceId)];

			await this.eFactVenteService.SynchroniserStatutActuelPieces(majStatutPieceRequest);
		}
	}

	// Annule la modification de statut
	public Annuler(): void {
		this.boiteDialogue.fonctionRetour(false);
		this.boiteDialogue.Fermer();
	}
}
